import React, { useState } from 'react';
import { useDeployments } from '../hooks/useDeployments';
import { useFeaturePacks } from '../hooks/useFeaturePacks';
import { DeploymentForm } from '../components/deployments/DeploymentForm';
import { DeploymentList } from '../components/deployments/DeploymentList';
import { Rocket, Plus, X } from 'lucide-react';
import { apiService } from '../services/api';
import { DeployRequest } from '../types/api';

export function Deployments() {
  const { deployments, loading, error, refetch } = useDeployments();
  const { featurePacks, loading: featurePacksLoading } = useFeaturePacks();
  const [showForm, setShowForm] = useState(false);
  const [deploying, setDeploying] = useState(false);

  const featurePackNames = featurePacks.map((fp) => fp.name);

  const handleDeploy = async (request: DeployRequest) => {
    setDeploying(true);
    try {
      await apiService.deployFeature(request);
      await refetch();
      setShowForm(false);
    } catch (err) {
      console.error('Failed to deploy:', err);
      throw err;
    } finally {
      setDeploying(false);
    }
  };

  const handleCancel = async (deploymentId: string) => {
    try {
      await apiService.cancelDeployment(deploymentId);
      await refetch();
    } catch (err) {
      console.error('Failed to cancel deployment:', err);
    }
  };

  const handleRollback = async (deploymentId: string) => {
    try {
      await apiService.rollbackDeployment(deploymentId);
      await refetch();
    } catch (err) {
      console.error('Failed to rollback deployment:', err);
    }
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Rocket className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Deployments</h1>
            <p className="text-gray-600 mt-1">Deploy features across your repository fleet</p>
          </div>
        </div>

        <button
          onClick={() => setShowForm(!showForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
        >
          {showForm ? (
            <>
              <X className="h-5 w-5" />
              Close
            </>
          ) : (
            <>
              <Plus className="h-5 w-5" />
              New Deployment
            </>
          )}
        </button>
      </div>

      {/* Deployment Form */}
      {showForm && (
        <DeploymentForm
          onSubmit={handleDeploy}
          featurePacks={featurePackNames}
          loading={deploying}
        />
      )}

      {/* Deployment List */}
      <DeploymentList
        deployments={deployments}
        loading={loading}
        error={error}
        onCancel={handleCancel}
        onRollback={handleRollback}
      />
    </div>
  );
}
